import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import SwedenMap from './SwedenMap';
import { ErrorBoundary } from 'react-error-boundary';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Tab } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';
import { Helmet } from 'react-helmet';

// Error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
        <div className="p-4 bg-red-500/10 rounded-lg border border-red-200">
            <h2 className="text-xl font-bold mb-2 text-red-700">Something went wrong:</h2>
            <pre className="text-sm text-red-600">{error.message}</pre>
            <button 
                onClick={resetErrorBoundary}
                className="mt-4 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
            >
                Try again
            </button>
        </div>
    );
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    annotationPlugin
);

// Validation helper
const validatePriceData = (data) => {
    if (!data || !data.hourly || !Array.isArray(data.hourly)) {
        return false;
    }
    return true;
};

// Data aggregation functions for zoom levels
const aggregateDataForZoom = (hourlyData, zoomLevel) => {
    if (!hourlyData || !Array.isArray(hourlyData)) return [];
    
    const is15MinData = hourlyData.length > 24;
    
    if (!is15MinData) {
        if (zoomLevel === '60min') return hourlyData;
        if (zoomLevel === '30min') {
            return hourlyData.flatMap(price => [price, price]);
        }
        return hourlyData.flatMap(price => [price, price, price, price]);
    }
    
    // 15-minute data aggregation
    switch (zoomLevel) {
        case '15min':
            return hourlyData;
        case '30min':
            const thirtyMinData = [];
            for (let i = 0; i < hourlyData.length; i += 2) {
                const chunk = hourlyData.slice(i, i + 2).filter(p => p !== null && !isNaN(p));
                if (chunk.length > 0) {
                    thirtyMinData.push(chunk.reduce((sum, price) => sum + price, 0) / chunk.length);
                } else {
                    thirtyMinData.push(null);
                }
            }
            return thirtyMinData;
        case '60min':
            const hourlyAggregated = [];
            for (let i = 0; i < hourlyData.length; i += 4) {
                const chunk = hourlyData.slice(i, i + 4).filter(p => p !== null && !isNaN(p));
                if (chunk.length > 0) {
                    hourlyAggregated.push(chunk.reduce((sum, price) => sum + price, 0) / chunk.length);
                } else {
                    hourlyAggregated.push(null);
                }
            }
            return hourlyAggregated;
        default:
            return hourlyData;
    }
};

const getZoomLevelInfo = (zoomLevel) => {
    switch (zoomLevel) {
        case '15min':
            return { points: 96, interval: 15, labelStep: 4 };
        case '30min':
            return { points: 48, interval: 30, labelStep: 2 };
        case '60min':
            return { points: 24, interval: 60, labelStep: 1 };
        default:
            return { points: 96, interval: 15, labelStep: 4 };
    }
};

const getCurrentPrice = (hourlyPrices, isClient = true, is15Min = false) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    
    if (!isClient) return hourlyPrices[0];
    
    const now = new Date();
    if (is15Min) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const quarterIndex = Math.floor(currentMinute / 15);
        let position = currentHour * 4 + quarterIndex;
        
        if (position >= hourlyPrices.length) {
            const proportion = (currentHour + currentMinute / 60) / 24;
            position = Math.floor(proportion * hourlyPrices.length);
        }
        
        return hourlyPrices[Math.min(position, hourlyPrices.length - 1)];
    } else {
        const currentHour = now.getHours();
        return hourlyPrices[Math.min(currentHour, hourlyPrices.length - 1)];
    }
};

const getLowestPrice = (hourlyPrices) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    
    const validPrices = hourlyPrices.filter(price => price !== null && price !== undefined && !isNaN(price));
    if (validPrices.length === 0) return null;
    
    const lowest = Math.min(...validPrices);
    const position = hourlyPrices.indexOf(lowest);
    
    return { price: lowest, position };
};

const getHighestPrice = (hourlyPrices) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    
    const validPrices = hourlyPrices.filter(price => price !== null && price !== undefined && !isNaN(price));
    if (validPrices.length === 0) return null;
    
    const highest = Math.max(...validPrices);
    const position = hourlyPrices.indexOf(highest);
    
    return { price: highest, position };
};

const formatHour = (position, is15Min = false) => {
    if (position === null || position === undefined || position < 0) {
        return '--:--';
    }
    
    if (is15Min) {
        if (position >= 96) {
            console.warn(`Invalid 15-min position: ${position}, using modulo`);
            position = position % 96;
        }
        const hour = Math.floor(position / 4);
        const minute = (position % 4) * 15;
        return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    } else {
        if (position >= 24) {
            console.warn(`Invalid hourly position: ${position}, using modulo`);
            position = position % 24;
        }
        return `${String(position).padStart(2, '0')}:00`;
    }
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

const MotalaPricingContent = ({
    defaultRegion = 'SE3',
    regions = ['SE1', 'SE2', 'SE3', 'SE4'],
    regionColors = {
        'SE1': '#d22530',
        'SE2': '#d22530',
        'SE3': '#d22530',
        'SE4': '#d22530'
    },
    getRegionColorClass = (region) => {
        const colors = {
            'SE1': 'bg-red-600',
            'SE2': 'bg-red-600',
            'SE3': 'bg-red-600',
            'SE4': 'bg-red-600'
        };
        return colors[region] || 'bg-red-600';
    },
    MapComponent = SwedenMap,
    apiEndpoint = '/api/prices',
    countryName = 'Sverige'
}) => {
    const [data, setData] = useState({ today: '', prices: {}, metadata: {}, predictedPrices: { tomorrow: {} } });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(defaultRegion);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [includeVAT, setIncludeVAT] = useState(false);
    const [predictedPrices, setPredictedPrices] = useState({ tomorrow: {} });
    const [selectedDate, setSelectedDate] = useState('today');
    const [isClient, setIsClient] = useState(false);
    const [zoomLevel, setZoomLevel] = useState('15min');

    const applyVAT = useCallback((price) => {
        if (price === null || price === undefined || isNaN(price)) return null;
        const numPrice = Number(price);
        if (isNaN(numPrice)) return null;
        return includeVAT ? (numPrice * 1.25) : numPrice;
    }, [includeVAT]);

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        const fetchPricesWithRetry = async (retryCount = 0) => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}${apiEndpoint}`);
                
                // Normalize the data structure to match API response
                const normalizedData = {
                    today: response.data.today || '',
                    metadata: response.data.metadata || {},
                    prices: {},
                    predictedPrices: response.data.predictedPrices || { tomorrow: {} }
                };

                // Handle both Swedish and Norwegian data structures
                if (response.data.prices) {
                    // Swedish structure - convert [area, data] arrays to objects
                    Object.entries(response.data.prices).forEach(([area, priceArray]) => {
                        if (Array.isArray(priceArray) && priceArray.length === 2) {
                            // If it's in [area, data] format from API
                            normalizedData.prices[priceArray[0]] = priceArray[1];
                        } else {
                            // Already in correct format
                            normalizedData.prices[area] = priceArray;
                        }
                    });
                } else if (response.data) {
                    // Norwegian structure - assuming direct region data
                    normalizedData.prices = response.data;
                }

                setData(normalizedData);
                setPredictedPrices(normalizedData.predictedPrices);
                setError(null);
                window.dispatchEvent(new Event('electricityPricingLoaded'));
            } catch (err) {
                console.error(`Attempt ${retryCount + 1} failed:`, err);
                if (retryCount < MAX_RETRIES) {
                    setTimeout(() => fetchPricesWithRetry(retryCount + 1), RETRY_DELAY);
                } else {
                    setError('Could not load electricity prices. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchPricesWithRetry();
    }, [apiEndpoint]);

    if (error) return <div className="text-red-600">{error}</div>;

    const generateChartData = (prices) => {
        // Determine if we're dealing with 15-minute data
        const firstRegionData = Object.values(prices)[0];
        
        // Check multiple indicators for 15-minute resolution
        const is15Min = firstRegionData?.resolution === 'PT15M' || 
                       firstRegionData?.is15Min === true || 
                       (firstRegionData?.dataPoints && firstRegionData.dataPoints === 96) ||
                       (firstRegionData?.hourly && firstRegionData.hourly.length === 96);
        
        // Get zoom level configuration
        const zoomInfo = getZoomLevelInfo(zoomLevel);
        
        // Generate labels based on zoom level
        const timeLabels = Array.from({ length: zoomInfo.points }, (_, i) => {
            const totalMinutes = i * zoomInfo.interval;
            const hour = Math.floor(totalMinutes / 60);
            const minute = totalMinutes % 60;
            
            if (hour >= 24) return '';
            
            return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        });
        
        const currentHour = isClient ? new Date().getHours() : 0;
        const currentMinute = isClient ? new Date().getMinutes() : 0;
        
        const getCurrentPosition = () => {
            if (!isClient) return 0;
            
            const totalCurrentMinutes = currentHour * 60 + currentMinute;
            return Math.floor(totalCurrentMinutes / zoomInfo.interval);
        };
        
        const currentPosition = getCurrentPosition();
        
        const datasets = Object.entries(prices).map(([region, data]) => {
            if (!validatePriceData(data)) {
                console.error(`Invalid data structure for region ${region}:`, data);
                return {
                    label: region,
                    data: Array(24).fill(null),
                    borderColor: regionColors[region] || '#d22530',
                    backgroundColor: regionColors[region] + '20',
                    fill: true,
                };
            }

            const aggregatedData = aggregateDataForZoom(data.hourly, zoomLevel);
            
            const paddedData = [...aggregatedData];
            while (paddedData.length < zoomInfo.points) {
                paddedData.push(null);
            }
            if (paddedData.length > zoomInfo.points) {
                paddedData.length = zoomInfo.points;
            }

            return {
                label: region,
                data: paddedData.map(price => price !== null ? applyVAT(price) : null),
                borderColor: regionColors[region],
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    
                    if (!chartArea) return regionColors[region] + '20';

                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, `${regionColors[region]}40`);
                    gradient.addColorStop(1, `${regionColors[region]}00`);
                    return gradient;
                },
                fill: 'start',
                stepped: 'before',
                tension: 0.4,
            };
        });

        return { labels: timeLabels, datasets, is15Min, zoomLevel, currentPosition };
    };

    const getCurrentDataset = () => {
        switch (selectedDate) {
            case 'tomorrow':
                return predictedPrices?.tomorrow || {};
            default:
                return data.prices;
        }
    };

    const getFormattedDate = () => {
        const date = new Date();
        switch (selectedDate) {
            case 'tomorrow':
                date.setDate(date.getDate() + 1);
                break;
            default:
                break;
        }
        return date.toLocaleDateString('sv-SE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 100,
        events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            annotation: {
                annotations: {
                    currentTime: {
                        display: selectedDate === 'today' && isClient,
                        type: 'line',
                        xMin: (() => {
                            if (!isClient) return 0;
                            const now = new Date();
                            const totalCurrentMinutes = now.getHours() * 60 + now.getMinutes();
                            const zoomInfo = getZoomLevelInfo(zoomLevel);
                            const position = Math.floor(totalCurrentMinutes / zoomInfo.interval);
                            return Math.min(position, zoomInfo.points - 1);
                        })(),
                        xMax: (() => {
                            if (!isClient) return 0;
                            const now = new Date();
                            const totalCurrentMinutes = now.getHours() * 60 + now.getMinutes();
                            const zoomInfo = getZoomLevelInfo(zoomLevel);
                            const position = Math.floor(totalCurrentMinutes / zoomInfo.interval);
                            return Math.min(position, zoomInfo.points - 1);
                        })(),
                        borderColor: 'rgba(210, 37, 48, 0.8)',
                        borderWidth: 2,
                        borderDash: [6, 6],
                        label: {
                            display: true,
                            backgroundColor: 'rgba(210, 37, 48, 0.9)',
                            content: (ctx) => {
                                const regionData = data.prices[selectedRegion];
                                const is15Min = regionData?.resolution === 'PT15M' || 
                                               regionData?.is15Min || 
                                               regionData?.dataPoints === 96;
                                const currentPrice = getCurrentPrice(regionData?.hourly, isClient, is15Min);
                                const appliedPrice = applyVAT(currentPrice);
                                return [`Nu`, `${appliedPrice !== null ? appliedPrice.toFixed(2) : '-'} öre/kWh`];
                            },
                            position: 'start',
                            padding: 6,
                            font: {
                                size: 12
                            },
                            color: '#ffffff'
                        }
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#374151',
                bodyColor: '#374151',
                padding: 12,
                titleFont: {
                    size: 14,
                    family: 'system-ui'
                },
                bodyFont: {
                    size: 13,
                    family: 'system-ui'
                },
                borderColor: 'rgba(210, 37, 48, 0.2)',
                borderWidth: 1,
                callbacks: {
                    label: function(tooltipItem) {
                        const zoomInfo = getZoomLevelInfo(zoomLevel);
                        
                        const startMinutes = tooltipItem.dataIndex * zoomInfo.interval;
                        const endMinutes = startMinutes + zoomInfo.interval;
                        
                        const startHour = Math.floor(startMinutes / 60);
                        const startMin = startMinutes % 60;
                        const endHour = Math.floor(endMinutes / 60);
                        const endMin = endMinutes % 60;
                        
                        let timeRange = '';
                        let isCurrentPeriod = false;
                        
                        if (startHour < 24) {
                            timeRange = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}–${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
                            
                            if (isClient) {
                                const now = new Date();
                                const currentMinutes = now.getHours() * 60 + now.getMinutes();
                                isCurrentPeriod = currentMinutes >= startMinutes && currentMinutes < endMinutes;
                            }
                        } else {
                            timeRange = 'Invalid time';
                        }
                        
                        const appliedPrice = applyVAT(tooltipItem.raw);
                        let label = `${tooltipItem.dataset.label}: ${appliedPrice !== null ? appliedPrice.toFixed(2) : '-'} öre/kWh`;
                        if (isCurrentPeriod) {
                            label += ' (Aktuellt pris)';
                        }
                        return `${label} (${timeRange})`;
                    },
                    title: function(tooltipItems) {
                        const item = tooltipItems[0];
                        const zoomInfo = getZoomLevelInfo(zoomLevel);
                        
                        if (isClient) {
                            const now = new Date();
                            const currentMinutes = now.getHours() * 60 + now.getMinutes();
                            const periodStart = item.dataIndex * zoomInfo.interval;
                            const periodEnd = periodStart + zoomInfo.interval;
                            
                            if (currentMinutes >= periodStart && currentMinutes < periodEnd) {
                                return `${item.label} (Aktuellt pris)`;
                            }
                        }
                        
                        return item.label;
                    }
                }
            },
            legend: {
                position: 'top',
                align: 'start',
                labels: {
                    color: '#374151',
                    padding: 20,
                    font: {
                        family: 'system-ui',
                        size: 13
                    },
                    usePointStyle: true,
                    pointStyle: 'rect'
                }
            }
        },
        scales: {
            y: {
                border: {
                    display: false
                },
                grid: {
                    color: 'rgba(210, 37, 48, 0.1)',
                    drawTicks: false
                },
                ticks: {
                    color: '#6B7280',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    callback: function(value) {
                        return value.toFixed(0) + ' öre';
                    }
                }
            },
            x: {
                border: {
                    display: false
                },
                grid: {
                    display: false
                },
                ticks: {
                    color: '#6B7280',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    callback: function(value, index) {
                        const zoomInfo = getZoomLevelInfo(zoomLevel);
                        
                        if (index % zoomInfo.labelStep === 0) {
                            const totalMinutes = index * zoomInfo.interval;
                            const hour = Math.floor(totalMinutes / 60);
                            const minute = totalMinutes % 60;
                            
                            if (hour < 24) {
                                return minute === 0 ? `${hour}:00` : `${hour}:${minute.toString().padStart(2, '0')}`;
                            }
                        }
                        return '';
                    }
                }
            }
        }
    };

    const MemoizedChart = React.memo(({ data, options }) => (
        <Line data={data} options={options} redraw={false} />
    ));

    const handleRegionSelect = (region) => {
        setSelectedRegion(region);
    };

    const renderChart = () => {
        try {
            const chartData = generateChartData(
                selectedRegion === 'ALL' 
                    ? getCurrentDataset() 
                    : { [selectedRegion]: getCurrentDataset()[selectedRegion] }
            );

            if (!chartData || !chartData.datasets || !chartData.labels) {
                console.error('Invalid chart data:', chartData);
                return <div className="text-red-600">Error: Invalid data format</div>;
            }

            return (
                <MemoizedChart 
                    data={chartData} 
                    options={chartOptions}
                />
            );
        } catch (error) {
            console.error('Chart rendering error:', error);
            return <div className="text-red-600">Error rendering chart</div>;
        }
    };

    return (
        <div className="min-h-screen overflow-y-auto relative bg-gradient-to-br from-white via-red-50 to-red-100 text-gray-800 py-8 md:py-12">
            <div className="container mx-auto px-4 md:px-8">                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
                    <div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                            {!loading && Object.entries(data.prices).map(([area, priceData]) => (
                                <div 
                                    key={area} 
                                    className={`bg-white border border-red-200 rounded-lg p-2 sm:p-4 cursor-pointer shadow-sm
                                        transform transition-all hover:-translate-y-1 hover:scale-102 hover:shadow-lg
                                        ${selectedRegion === area ? 'ring-2 ring-red-400' : ''}`}
                                    onClick={() => handleRegionSelect(area)}
                                >
                                    <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(area)}`}></div>
                                    <div className="text-center">
                                        <h3 className="text-base sm:text-lg font-bold text-gray-800">{area}</h3>
                                        <p className="text-xl sm:text-2xl font-extrabold mt-0.5 sm:mt-1 text-red-600">
                                            {(() => {
                                                const price = applyVAT(parseFloat(priceData.average));
                                                return price !== null ? price.toFixed(2) : '-';
                                            })()}
                                            <span className="text-sm sm:text-base opacity-70 ml-1">öre</span>
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {loading && Array(4).fill(0).map((_, index) => (
                                <div key={index} className="animate-pulse bg-gray-200 rounded-lg h-[80px] sm:h-[120px]" />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white border border-red-200 rounded-2xl p-6 shadow-sm">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                                <div className="mb-4 md:mb-0">
                                    <h2 className="text-2xl font-bold text-gray-800">{getFormattedDate()}</h2>
                                    {!loading && (
                                        <div className="flex items-center mt-2 text-sm text-gray-600">
                                            <div className={`w-2 h-2 rounded-full mr-2 ${
                                                zoomLevel === '15min' ? 'bg-green-400' : 
                                                zoomLevel === '30min' ? 'bg-yellow-400' : 'bg-blue-400'
                                            }`}></div>
                                            {zoomLevel === '15min' && '15-minuters intervaller'}
                                            {zoomLevel === '30min' && '30-minuters intervaller'}
                                            {zoomLevel === '60min' && '60-minuters intervaller'}
                                            {data.metadata?.resolution === 'PT15M' || data.metadata?.is15MinResolution ? 
                                                ` (${getZoomLevelInfo(zoomLevel).points} visade punkter)` : 
                                                ` (${getZoomLevelInfo(zoomLevel).points} datapunkter)`
                                            }
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col md:flex-row items-start md:items-center space-y-3 md:space-y-0 md:space-x-4 w-full md:w-auto">
                                    <div className="flex items-center space-x-2 justify-center md:justify-start w-full md:w-auto">
                                        <span className="text-sm text-gray-600">Visa priser</span>
                                        <Switch
                                            checked={includeVAT}
                                            onChange={setIncludeVAT}
                                            className={`${
                                                includeVAT ? 'bg-red-600' : 'bg-gray-200'
                                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                                        >
                                            <span className="sr-only">Visa priser med moms</span>
                                            <span
                                                className={`${
                                                    includeVAT ? 'translate-x-6' : 'translate-x-1'
                                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                            />
                                        </Switch>
                                        <span className="text-sm text-gray-600">{includeVAT ? 'inkl. moms' : 'exkl. moms'}</span>
                                    </div>
                                    
                                    <div className="flex justify-between md:justify-start items-center gap-3 md:gap-4 w-full md:w-auto">
                                        <div className="flex space-x-2">
                                            <button 
                                                onClick={() => setSelectedDate('today')}
                                                className={`px-3 py-2 text-xs rounded-md font-medium ${
                                                    selectedDate === 'today' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                                }`}
                                            >
                                                Idag
                                            </button>
                                            <button 
                                                onClick={() => setSelectedDate('tomorrow')}
                                                className={`px-3 py-2 text-xs rounded-md font-medium ${
                                                    selectedDate === 'tomorrow' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                                }`}
                                            >
                                                Imorgon
                                            </button>
                                        </div>
                                        
                                        <div className="flex space-x-2">
                                            <button 
                                                onClick={() => setZoomLevel('15min')}
                                                className={`px-2 py-1 text-xs rounded-md font-medium ${
                                                    zoomLevel === '15min' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                                }`}
                                            >
                                                15min
                                            </button>
                                            <button 
                                                onClick={() => setZoomLevel('30min')}
                                                className={`px-2 py-1 text-xs rounded-md font-medium ${
                                                    zoomLevel === '30min' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                                                }`}
                                            >
                                                30min
                                            </button>
                                            <button 
                                                onClick={() => setZoomLevel('60min')}
                                                className={`px-2 py-1 text-xs rounded-md font-medium ${
                                                    zoomLevel === '60min' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                                                }`}
                                            >
                                                1h
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="h-96 w-full">
                                {loading ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                                    </div>
                                ) : (
                                    <ErrorBoundary 
                                        FallbackComponent={ErrorFallback}
                                        onReset={() => window.location.reload()}
                                    >
                                        {renderChart()}
                                    </ErrorBoundary>
                                )}
                            </div>
                        </div>

                        {/* Price Statistics Cards - moved below chart */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Current Price Card */}
                            <div className="bg-white border border-red-200 rounded-2xl p-4 shadow-sm">
                                <h3 className="text-sm font-bold mb-2 flex items-center text-gray-800">
                                    <InformationCircleIcon className="h-4 w-4 mr-2 text-red-600" />
                                    Aktuellt pris - {selectedRegion}
                                </h3>
                                {loading ? (
                                    <Skeleton height={30} baseColor="#f3f4f6" highlightColor="#ffffff" />
                                ) : (
                                    <div className="text-center">
                                        <div className="text-xl font-bold text-red-600">
                                            {(() => {
                                                const regionData = getCurrentDataset()[selectedRegion];
                                                if (!regionData) return '-';
                                                
                                                const is15Min = regionData.resolution === 'PT15M' || 
                                                               regionData.is15Min || 
                                                               regionData.dataPoints === 96;
                                                const currentPrice = getCurrentPrice(regionData.hourly, isClient, is15Min);
                                                const appliedPrice = applyVAT(currentPrice);
                                                return appliedPrice !== null ? appliedPrice.toFixed(2) : '-';
                                            })()}
                                            <span className="text-sm opacity-70 ml-1">öre/kWh</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {isClient ? `${new Date().getHours().toString().padStart(2, '0')}:${new Date().getMinutes().toString().padStart(2, '0')}` : 'Nu'}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Lowest Price */}
                            <div className="bg-white border border-red-200 rounded-2xl p-4 shadow-sm">
                                <h4 className="text-sm font-semibold text-green-600 mb-2">Lägsta pris idag</h4>
                                {loading ? (
                                    <Skeleton height={30} baseColor="#f3f4f6" highlightColor="#ffffff" />
                                ) : (
                                    <div>
                                        {(() => {
                                            const regionData = getCurrentDataset()[selectedRegion];
                                            if (!regionData) return <span>-</span>;
                                            
                                            const is15Min = regionData.resolution === 'PT15M' || 
                                                           regionData.is15Min || 
                                                           regionData.dataPoints === 96;
                                            const lowest = getLowestPrice(regionData.hourly);
                                            if (!lowest) return <span>-</span>;
                                            
                                            const appliedPrice = applyVAT(lowest.price);
                                            return (
                                                <div>
                                                    <div className="text-xl font-bold text-green-600">
                                                        {appliedPrice !== null ? appliedPrice.toFixed(2) : '-'}
                                                        <span className="text-sm opacity-70 ml-1">öre</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {formatHour(lowest.position, is15Min)}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>

                            {/* Highest Price */}
                            <div className="bg-white border border-red-200 rounded-2xl p-4 shadow-sm">
                                <h4 className="text-sm font-semibold text-red-600 mb-2">Högsta pris idag</h4>
                                {loading ? (
                                    <Skeleton height={30} baseColor="#f3f4f6" highlightColor="#ffffff" />
                                ) : (
                                    <div>
                                        {(() => {
                                            const regionData = getCurrentDataset()[selectedRegion];
                                            if (!regionData) return <span>-</span>;
                                            
                                            const is15Min = regionData.resolution === 'PT15M' || 
                                                           regionData.is15Min || 
                                                           regionData.dataPoints === 96;
                                            const highest = getHighestPrice(regionData.hourly);
                                            if (!highest) return <span>-</span>;
                                            
                                            const appliedPrice = applyVAT(highest.price);
                                            return (
                                                <div>
                                                    <div className="text-xl font-bold text-red-600">
                                                        {appliedPrice !== null ? appliedPrice.toFixed(2) : '-'}
                                                        <span className="text-sm opacity-70 ml-1">öre</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {formatHour(highest.position, is15Min)}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>

                            {/* Average Price */}
                            <div className="bg-white border border-red-200 rounded-2xl p-4 shadow-sm">
                                <h4 className="text-sm font-semibold text-blue-600 mb-2">Genomsnittspris</h4>
                                {loading ? (
                                    <Skeleton height={30} baseColor="#f3f4f6" highlightColor="#ffffff" />
                                ) : (
                                    <div className="text-xl font-bold text-blue-600">
                                        {(() => {
                                            const regionData = getCurrentDataset()[selectedRegion];
                                            if (!regionData || !regionData.average) return '-';
                                            
                                            const appliedPrice = applyVAT(parseFloat(regionData.average));
                                            return appliedPrice !== null ? appliedPrice.toFixed(2) : '-';
                                        })()}
                                        <span className="text-sm opacity-70 ml-1">öre/kWh</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="space-y-6">
                        {/* Map Component - moved to right side */}
                        {MapComponent && (
                            <div className="bg-white border border-red-200 rounded-2xl p-6 shadow-sm">
                                <h3 className="text-lg font-bold mb-4 text-gray-800">Elområden</h3>
                                <MapComponent
                                    data={getCurrentDataset()}
                                    selectedRegion={selectedRegion}
                                    onRegionSelect={handleRegionSelect}
                                    applyVAT={applyVAT}
                                    loading={loading}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main component wrapper with error boundary
const MotalaPricing = (props) => {
    return (
        <>
            <Helmet>
                <title>Elpriser i Sverige - Motala Energi</title>
                <meta name="description" content="Se aktuella elpriser för alla svenska elområden. Jämför priser mellan SE1, SE2, SE3 och SE4 med realtidsdata." />
                <meta name="keywords" content="elpriser, sverige, SE1, SE2, SE3, SE4, elektricitet, energi, motala energi" />
                <link rel="canonical" href="https://motalaenergi.se/elpriser" />
            </Helmet>
            <ErrorBoundary 
                FallbackComponent={ErrorFallback}
                onReset={() => window.location.reload()}
            >
                <MotalaPricingContent {...props} />
            </ErrorBoundary>
        </>
    );
};

export default MotalaPricing;